import { Inbox, BookOpen, GitBranch } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useLoad, Loading, EmptyState, Panel, DataTable, StatusBadge, fmtDate } from "./shared";

const LEAD_STATUSES = ["new", "contacted", "qualified", "proposal_sent", "won", "lost", "disqualified"];

const Who = ({ name, email, company }) => (
  <div><p className="font-medium text-slate-900">{name}</p><p className="text-xs text-slate-500">{email}{company ? ` · ${company}` : ""}</p></div>
);

const Enquiries = ({ call }) => {
  const { data, loading, reload } = useLoad(call, "/leads");
  if (loading) return <Loading />;
  const leads = data?.leads || [];
  if (!leads.length) return <EmptyState testid="leads-empty" icon={Inbox} title="No enquiries yet" text="Website enquiries from /commerce/contact will appear here." />;
  const setStatus = async (id, status) => {
    await call(`/leads/${id}`, { method: "PATCH", body: { status } });
    toast.success("Lead updated"); reload();
  };
  return (
    <Panel>
      <DataTable testid="leads-table" rowKey="enquiry_id" rows={leads} columns={[
        { key: "who", label: "Contact", render: r => <Who name={r.full_name} email={r.email} company={r.company_name} /> },
        { key: "services", label: "Interest", render: r => <span className="text-slate-600">{(r.services_needed || []).join(", ") || "General"}</span> },
        { key: "budget", label: "Budget" },
        { key: "description", label: "Message", render: r => <p className="max-w-xs text-xs text-slate-500 line-clamp-3">{r.description || "—"}</p> },
        { key: "source", label: "Source", render: r => <span className="capitalize">{r.source}</span> },
        { key: "created_at", label: "Received", render: r => fmtDate(r.created_at) },
        { key: "status", label: "Status", render: r => (
          <Select value={r.status} onValueChange={v => setStatus(r.enquiry_id, v)}>
            <SelectTrigger className="h-8 w-36" data-testid={`lead-status-${r.enquiry_id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{LEAD_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
          </Select>
        ) },
      ]} />
    </Panel>
  );
};

const PlaybookLeads = ({ call }) => {
  const { data, loading } = useLoad(call, "/playbook-requests");
  if (loading) return <Loading />;
  const rows = data?.requests || [];
  if (!rows.length) return <EmptyState testid="playbook-leads-empty" icon={BookOpen} title="No playbook downloads yet" text="Leads from /commerce/playbook will appear here." />;
  return (
    <Panel>
      <DataTable testid="playbook-leads-table" rowKey="request_id" rows={rows} columns={[
        { key: "who", label: "Contact", render: r => <Who name={r.full_name} email={r.email} company={r.company_name} /> },
        { key: "marketing_consent", label: "Marketing consent", render: r => <StatusBadge status={r.marketing_consent ? "accepted" : "declined"} /> },
        { key: "requests", label: "Requests" },
        { key: "downloads", label: "Downloads" },
        { key: "created_at", label: "First requested", render: r => fmtDate(r.created_at) },
      ]} />
    </Panel>
  );
};

const CrmPipeline = ({ call }) => {
  const { data, loading } = useLoad(call, "/crm");
  if (loading || !data) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-wrap gap-6 items-center" data-testid="commerce-crm-org">
        <div><p className="text-xs text-slate-500">CRM organisation</p><p className="font-semibold">{data.organization?.business_name}</p></div>
        <div><p className="text-xs text-slate-500">Org ID</p><p className="font-mono text-xs">{data.organization?.org_id}</p></div>
        <div><p className="text-xs text-slate-500">Contacts</p><p className="font-semibold">{data.contacts_count}</p></div>
        {Object.entries(data.stage_counts).map(([k, v]) => (
          <div key={k}><p className="text-xs text-slate-500 capitalize">{k}</p><p className="font-semibold">{v}</p></div>
        ))}
      </div>
      {data.deals.length ? (
        <Panel title="Deals (Commerce organisation only)">
          <DataTable testid="commerce-crm-deals" rowKey="deal_id" rows={data.deals} columns={[
            { key: "name", label: "Deal", render: r => <span className="font-medium">{r.name}</span> },
            { key: "contact_name", label: "Contact" },
            { key: "stage", label: "CRM stage", render: r => <StatusBadge status={r.stage} /> },
            { key: "source", label: "Source", render: r => <span className="capitalize">{r.source}</span> },
            { key: "updated_at", label: "Updated", render: r => fmtDate(r.updated_at) },
          ]} />
        </Panel>
      ) : <EmptyState icon={GitBranch} title="No deals yet" />}
    </div>
  );
};

export const CommerceLeads = ({ call }) => (
  <Tabs defaultValue="enquiries" data-testid="commerce-leads">
    <TabsList>
      <TabsTrigger value="enquiries" data-testid="leads-tab-enquiries">Website enquiries</TabsTrigger>
      <TabsTrigger value="playbook" data-testid="leads-tab-playbook">Playbook leads</TabsTrigger>
      <TabsTrigger value="crm" data-testid="leads-tab-crm">CRM pipeline</TabsTrigger>
    </TabsList>
    <TabsContent value="enquiries" className="mt-4"><Enquiries call={call} /></TabsContent>
    <TabsContent value="playbook" className="mt-4"><PlaybookLeads call={call} /></TabsContent>
    <TabsContent value="crm" className="mt-4"><CrmPipeline call={call} /></TabsContent>
  </Tabs>
);
